import React, { Component, Fragment } from 'react';
import styleable from 'react-styleable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { DataTable, Button, DatePicker } from '@3plearning/react-components';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import css from './student.scss';
import studentActions, {
    ACTIVITY_MASTERY_STUDENT_RETRIEVED
} from './student-actions';
import {
    ALL_RESULTS_TABLE_CONFIG,
    TOPIC_NAME,
    TOPIC_RESULTS,
    TOTAL_ACTIVITIES,
    CLASS_NAME,
    TABLE_TABS,
    ALL_RESULTS_TAB,
    TEST_RESULTS_TAB,
    TEST_RESULTS_TABLE_CONFIG,
    TEST_ATTEMPTS
} from './student-constants';
import TopicActivityStrengthWeakness from '../common/topic-activity-strength-weakness/topic-activity-strength-weakness';
import {
    SELECTED_DATE,
    SELECTED_STUDENT,
    SELECTED_CLASS
} from '../../../components/report-filters/filter-constants';
import ActivityStatusBar from '../../../components/activity-status-bar/activity-status-bar';
import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';
import activeFiltersActions from '../../../components/report-filters/redux/active-filters/active-filters-actions';
import studentPropTypes from './student-prop-types';
import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';
import FilterHelper from '../../../helpers/filter-helper';
import ActivityMasteryLegends from '../common/activity-mastery-legends/activity-mastery-legends';
import { translateTableLabels } from '../../../helpers/locale-helpers';

/**
 * Student component
 * Displays Activity Mastery table and strengths & weaknesses tabs for a student
 */
export class Student extends Component {
    static propTypes = {
        activityMasteryData: studentPropTypes.activityMasteryData,
        getActivityMasteryData: PropTypes.func,
        activeFilters: activeFiltersPropType,
        invalidRequest: PropTypes.string,
        updateActiveFilter: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.state = {
            tableConfig: null,
            studentData: [],
            tableTab: ALL_RESULTS_TAB,
            strengthData: { topics: [], activties: [] },
            weaknessData: { topics: [], activities: [] },
            allResultsConfig: ALL_RESULTS_TABLE_CONFIG,
            testResultsConfig: TEST_RESULTS_TABLE_CONFIG
        };
        this.i18n = window.i18n;
    }

    componentDidMount() {
        const { activeFilters } = this.props;
        if (FilterHelper.shouldLoadStudent(activeFilters)) {
            this.getStudentActivityMasteryData(
                activeFilters[SELECTED_STUDENT].id,
                activeFilters[SELECTED_CLASS].id,
                activeFilters[SELECTED_DATE]
            );
        }
    }

    componentWillReceiveProps(newProps) {
        const {
            activityMasteryData,
            activeFilters,
            invalidRequest,
            updateActiveFilter
        } = this.props;
        const {
            activityMasteryData: newActivityMasteryData,
            activeFilters: newActiveFilters,
            invalidRequest: newInvalidRequest
        } = newProps;

        if (FilterHelper.shouldReloadStudent(activeFilters, newActiveFilters)) {
            this.getStudentActivityMasteryData(
                newActiveFilters[SELECTED_STUDENT].id,
                newActiveFilters[SELECTED_CLASS].id,
                newActiveFilters[SELECTED_DATE]
            );
        }

        if (activityMasteryData !== newActivityMasteryData) {
            this.configureTable(newActivityMasteryData);
        }

        if (
            invalidRequest !== newInvalidRequest &&
            newInvalidRequest === ACTIVITY_MASTERY_STUDENT_RETRIEVED
        ) {
            if (
                newActiveFilters[SELECTED_DATE].type !==
                DatePicker.THIS_SCHOOL_YEAR
            ) {
                updateActiveFilter(SELECTED_DATE, {
                    type: DatePicker.THIS_SCHOOL_YEAR
                });
            }
        }
    }

    shouldComponentUpdate(newProps, newState) {
        const stateToTriggerRender = [
            'tableConfig',
            'tableTab',
            'studentData',
            'strengthData',
            'weaknessData'
        ];
        return !!stateToTriggerRender.find(
            (key) => newState[key] !== this.state[key]
        );
    }

    getStudentActivityMasteryData(studentId, classId, selectedDateFilter) {
        this.props.getActivityMasteryData(
            classId,
            studentId,
            selectedDateFilter
        );
    }

    setTableTab = (tab) => {
        this.setState((prevState) => {
            if (prevState.tableTab !== tab) {
                setTimeout(() => {
                    const { activityMasteryData } = this.props;
                    this.configureTable(activityMasteryData);
                }, 1);
                return { tableTab: tab };
            }
            return null;
        });
    };

    /**
     * Sets table config and data values to use according to the current selected tab (All results or Test results)
     */
    configureTable = (activityMasteryData) => {
        this.setState((prevState) => {
            const allResultsConfig = this.generateAllResultsConfig(
                activityMasteryData
            );
            const testResultsConfig = this.generateTestResultsConfig(
                activityMasteryData
            );
            let tableConfig;
            let studentData;

            if (prevState.tableTab === ALL_RESULTS_TAB) {
                tableConfig = allResultsConfig;
                studentData = activityMasteryData.topicActivityResults;
            } else {
                tableConfig = testResultsConfig;
                studentData = activityMasteryData.testResults;
            }

            return {
                tableConfig,
                studentData,
                strengthData: activityMasteryData.strengths,
                weaknessData: activityMasteryData.weaknesses,
                allResultsConfig,
                testResultsConfig
            };
        });
    };

    /**
     * Generates the table config for the All results tab using the student activity mastery data
     * Some of the config is dynamic and requires data from the backend
     */
    generateAllResultsConfig(activityMasteryData) {
        let tableConfig = this.generateBaseConfig(
            cloneDeep(ALL_RESULTS_TABLE_CONFIG),
            activityMasteryData
        );

        const courseActivityMastery = pick(activityMasteryData.courseInfo, [
            'scoreBand',
            'goldBars',
            'totalActivities'
        ]);
        this.getColFromConfig(
            tableConfig,
            TOPIC_RESULTS
        ).headerTransform = () => (
            <ActivityStatusBar activityMastery={courseActivityMastery} />
        );

        this.getColFromConfig(
            tableConfig,
            TOTAL_ACTIVITIES
        ).name = `${activityMasteryData.courseInfo.totalActivities}`;

        tableConfig = translateTableLabels(tableConfig);
        return tableConfig;
    }

    /**
     * Generates the table config for the Test results tab using the student activity mastery data
     * Some of the config is dynamic and requires data from the backend
     */
    generateTestResultsConfig(activityMasteryData) {
        const tableConfig = this.generateBaseConfig(
            cloneDeep(TEST_RESULTS_TABLE_CONFIG),
            activityMasteryData
        );

        this.getColFromConfig(
            tableConfig,
            TEST_ATTEMPTS
        ).name = `${activityMasteryData.courseInfo.totalTests}`;

        return tableConfig;
    }

    /**
     * Generates the table config common between All results and Test results tabs
     */
    generateBaseConfig(baseConfig, activityMasteryData) {
        this.getColGroupFromConfig(baseConfig, CLASS_NAME).name =
            activityMasteryData.className;

        this.getColGroupFromConfig(
            baseConfig,
            TABLE_TABS
        ).headerTransform = this.renderTableTabs;

        this.getColFromConfig(baseConfig, TOPIC_NAME).name =
            activityMasteryData.courseInfo.name;

        return baseConfig;
    }

    getColFromConfig = (config, colId) =>
        config.cols.find((col) => col.id === colId);

    getColGroupFromConfig = (config, colGroupId) => {
        let colGroup;
        config.colGroups.forEach((colGroupRow) => {
            if (!colGroup) {
                colGroup = colGroupRow.find((c) => c.id === colGroupId);
            }
        });
        return colGroup;
    };

    renderTableTabs = () => {
        const { tableTab } = this.state;
        const buttons = [
            {
                tab: ALL_RESULTS_TAB,
                text: this.i18n._('Label - AllResults')
            },
            {
                tab: TEST_RESULTS_TAB,
                text: this.i18n._('Label - TestResults')
            }
        ];
        return (
            <div className={css['table-tab-container']}>
                {buttons.map((button) => {
                    const isActive = tableTab === button.tab;
                    let className = css['table-tab-btn'];
                    if (isActive) {
                        className += ` ${css.active}`;
                    }
                    return (
                        <Button
                            key={button.tab}
                            text={button.text}
                            className={className}
                            onClick={() => this.setTableTab(button.tab)}
                        />
                    );
                })}
            </div>
        );
    };

    render() {
        const {
            tableConfig,
            studentData,
            strengthData,
            weaknessData
        } = this.state;

        return (
            <Fragment>
                <ActivityMasteryLegends />
                <div className={css.component}>
                    <div className={css['data-table']}>
                        <DataTable config={tableConfig} data={studentData} />
                    </div>
                    <div className={css['strength-weakness']}>
                        <TopicActivityStrengthWeakness
                            strengths={strengthData}
                            weaknesses={weaknessData}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        activityMasteryData: state.activityMasteryStudent,
        invalidRequest: state.invalidRequest
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            getActivityMasteryData: studentActions.getActivityMasteryData,
            updateActiveFilter: activeFiltersActions.updateActiveFilter
        },
        dispatch
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(styleable(css)(Student));



// WEBPACK FOOTER //
// ./containers/activity-mastery/student/student.js